import React, { useState, useEffect, useRef } from 'react';
import {
  Box, IconButton, TextField, Drawer, Typography, Avatar, Paper,
  useTheme, useMediaQuery, Fab, CircularProgress, Tooltip, Zoom
} from '@mui/material';
import {
  Send, Close, Delete,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import utils from '../utils';

const AiChat = ({ startsOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isOpen, setIsOpen] = useState(startsOpen ?? false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [userId, setUserId] = useState(0);
  const [role, setRole] = useState("");

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Colori personalizzati con header dodger blue
  const colors = {
    primary: '#3a86ff',
    secondary: '#ff006e',
    background: '#f8f9fa',
    header: '#1E90FF',  // Dodger Blue
    userBubble: '#3a86ff',
    aiBubble: '#f1f3f4',
    aiText: '#202124',
    headerText: '#ffffff',  // Testo bianco per l'header
    iconColor: '#ffffff'  // Icone bianche per l'header
  };

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    if (role == 'operator') {
      loadMessages();
    }
  }, [role]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(scrollToBottom, 100); // Attendi il rendering prima dello scroll
    }
  }, [isOpen]);

  const loadUser = async () => {
    try {
      const res = await axios.get("/api/me", {
        headers: utils.getAuthHeaders(),
        validateStatus: () => true,
      });
      if (res.status === 200) {
        const { success, me } = res.data;
        if (success) {
          setUserId(me.id);
          setRole(me.op ? 'operator' : 'client');
        }
      }
    } catch (_) { }
  }

  // Funzione per gestire il contenuto multilinea
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift+Enter: inserisce una nuova riga
        return; // TextField gestirà l'inserimento del newline
      } else if (!isLoading) {
        // Solo Enter (senza Shift): invia il messaggio
        e.preventDefault();
        handleSendMessage();
      }
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || isLoading) return;

    // Aggiungi immediatamente il messaggio dell'utente
    // Preserva le interruzioni di riga nel messaggio
    const userMessage = {
      text: message,
      sender: 'user',
      timestamp: new Date().getTime()
    };
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setMessage("");
    setIsLoading(true);

    // Simula l'effetto "sta scrivendo"
    setTimeout(() => setIsTyping(true), 500);

    try {
      // Invia il messaggio all'API di Mistral
      const response = await axios.post('/api/mistral', { message }, {
        headers: utils.getAuthHeaders(),
      });

      // Nascondi l'indicatore "sta scrivendo"
      setIsTyping(false);

      const { success } = response.data;
      if (success) {
        // Aggiungi la risposta dell'IA
        setMessages(prevMessages => [
          ...prevMessages,
          { text: response.data.response, sender: 'ai', timestamp: new Date().getTime() }
        ]);
      } else {
        setMessages(prevMessages => [
          ...prevMessages,
          {
            text: "Mi dispiace, si è verificato un errore. Riprova più tardi.",
            sender: 'ai',
            error: true,
            timestamp: new Date().getTime()
          }
        ]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setIsTyping(false);
      // Mostra un messaggio di errore nella chat
      setMessages(prevMessages => [
        ...prevMessages,
        {
          text: "Mi dispiace, si è verificato un errore. Riprova più tardi.",
          sender: 'ai',
          error: true,
          timestamp: new Date().getTime()
        }
      ]);
    }

    setIsLoading(false);
  };

  const loadMessages = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get('/api/mistral', {
        headers: utils.getAuthHeaders(),
      });
      const { success, error, messages } = res.data;
      if (success) {
        setMessages(messages);
      }
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  };

  const clearConversation = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post('/api/mistralclear', {}, {
        headers: utils.getAuthHeaders(),
      });
      const { success, error } = res.data;
      if (success) {
        setMessages([]);
      }
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return `${date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  // Funzione per formattare correttamente il messaggio con interruzioni di riga
  const formatMessage = (text) => {
    return text.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    // Focus sull'input quando si apre la chat
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);

  if (role !== 'operator') {
    return null;
  }

  return (
    <>
      {/* Icona fluttuante originale per aprire la chat */}
      <Tooltip title="Assistente AI" placement="left" TransitionComponent={Zoom}>
        <Fab
          size="small"
          onClick={() => setIsOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 155,
            right: 10,
            zIndex: 1000,
            padding: 0,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: 'rgba(0, 127, 255, 0.2)',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <motion.div
            whileHover={{ scale: [1, 1.1, 1] }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
              repeat: 3, // Ripete l'animazione 3 volte
              repeatDelay: 0.1 // Piccolo ritardo tra le ripetizioni
            }}
          >
            <Avatar alt="AI" src="/img/happy.png" />
          </motion.div>
        </Fab>
      </Tooltip>

      {/* Chat drawer con dimensioni originali */}
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 700,
            height: '100%',
            borderRadius: isMobile ? 0 : 4,
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header con sfondo Dodger Blue */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: colors.header,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              zIndex: 10,
            }}
          >
            <Avatar
              alt="AI"
              src="/img/happy.png"
              sx={{
                mr: 1.5,
                width: 40,
                height: 40,
                bgcolor: 'transparent', // Sfondo trasparente per l'avatar
                animation: 'rotate 3s infinite linear', // Aggiunge un'animazione di rotazione
                '@keyframes rotate': {
                  '0%': {
                    transform: 'rotate(0deg)',
                  },
                  '50%': {
                    transform: 'rotate(10deg)', // Ruota leggermente a destra
                  },
                  '100%': {
                    transform: 'rotate(0deg)',
                  },
                },
              }}
            />

            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="600" color={colors.headerText}>
                Romeo AI
              </Typography>
              <Typography
                variant="caption"
                color="white"
                sx={{
                  fontStyle: 'italic',
                  fontSize: '0.6rem',
                  ml: 1,
                }}
              >
                Sempre online
              </Typography>
            </Box>

            <Tooltip title="Cancella conversazione" placement="top">
              <IconButton
                onClick={clearConversation}
                disabled={messages.length === 0 || isLoading}
                sx={{
                  mr: 1,
                  color: messages.length === 0 ? 'rgba(255, 255, 255, 0.5)' : colors.headerText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={() => setIsOpen(false)}
              sx={{
                color: colors.headerText,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <Close />
            </IconButton>
          </Paper>

          {/* Area messaggi */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 2,
              bgcolor: colors.background,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {messages.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  opacity: 0.7
                }}
              >
                <Avatar
                  alt="AI"
                  src="/img/happy.png"
                  sx={{
                    width: 64,
                    height: 64,
                    mb: 2
                  }}
                />
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  Inizia una conversazione con l'assistente AI
                </Typography>
              </Box>
            ) : (
              <AnimatePresence>
                {messages.map((msg) => (
                  <motion.div
                    key={msg.index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                        mb: 2,
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          maxWidth: '70%',
                          backgroundColor: msg.sender === 'user' ? colors.userBubble : colors.aiBubble,
                          color: msg.sender === 'user' ? '#fff' : colors.aiText,
                          borderRadius: msg.sender === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                          fontWeight: msg.error ? 500 : 400,
                          boxShadow: msg.error ? `0 0 0 1px dodgerblue` : 'none', // Colore Contorno Bubble dell Ai
                          border: msg.sender === 'ai' ? `1px solid ${colors.header}` : 'none', // Aggiunge il contorno
                        }}
                      >
                        <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
                          {formatMessage(msg.text)}
                        </Typography>
                      </Paper>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, mr: msg.sender === 'user' ? 1 : 0, ml: msg.sender === 'ai' ? 1 : 0 }}
                      >
                        {formatTime(msg.timestamp)}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}

                {/* Indicatore "sta scrivendo" */}
                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        mb: 2,
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          maxWidth: '60%',
                          backgroundColor: colors.aiBubble,
                          borderRadius: '18px 18px 18px 4px',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: colors.primary,
                              mr: 0.5,
                              animation: 'pulse 1s infinite',
                              '&:nth-of-type(2)': {
                                animationDelay: '0.2s',
                              },
                              '&:nth-of-type(3)': {
                                animationDelay: '0.4s',
                              },
                              '@keyframes pulse': {
                                '0%': {
                                  transform: 'scale(0.8)',
                                  opacity: 0.5,
                                },
                                '50%': {
                                  transform: 'scale(1)',
                                  opacity: 1,
                                },
                                '100%': {
                                  transform: 'scale(0.8)',
                                  opacity: 0.5,
                                },
                              },
                            }}
                          />
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: colors.primary,
                              mr: 0.5,
                              animation: 'pulse 1s infinite',
                              animationDelay: '0.2s',
                            }}
                          />
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: colors.primary,
                              animation: 'pulse 1s infinite',
                              animationDelay: '0.4s',
                            }}
                          />
                        </Box>
                      </Paper>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Area input con supporto multilinea */}
          <Paper
            elevation={3}
            sx={{
              p: 1,
              display: 'flex',
              alignItems: 'center',
              borderTop: 1,
              borderColor: 'divider',
              boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.1)',
            }}
          >
            <TextField
              fullWidth
              inputRef={inputRef}
              size="small"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Scrivi un messaggio..."
              onKeyDown={handleKeyDown}
              variant="outlined"
              multiline
              maxRows={4}
              sx={{
                borderRadius: '20px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                  padding: '8px 14px',
                }
              }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={handleSendMessage}
                    disabled={isLoading || !message.trim()}
                    sx={{
                      bgcolor: 'transparent', // Rimuove lo sfondo
                      color: message.trim() ? colors.primary : 'text.disabled', // Mantiene il colore dell'icona
                      '&:hover': {
                        bgcolor: 'transparent', // Rimuove lo sfondo al passaggio del mouse
                        opacity: message.trim() ? 0.7 : 1, // Aggiunge un effetto di opacità al passaggio del mouse
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'transparent', // Rimuove lo sfondo quando disabilitato
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} thickness={4} sx={{ color: colors.primary }} />
                    ) : (
                      <Send />
                    )}
                  </IconButton>
                ),
                style: {
                  borderRadius: '20px',
                },
              }}
            />
          </Paper>
        </Box>
      </Drawer>

      {/* Stile globale per le animazioni */}
      <style jsx global>{`
        @keyframes pulse {
          0% { transform: scale(0.8); opacity: 0.5; }
          50% { transform: scale(1); opacity: 1; }
          100% { transform: scale(0.8); opacity: 0.5; }
        }
      `}</style>
    </>
  );
};

export default AiChat;